import React, { useState, useRef, useEffect } from "react";
import { Modal, Box } from "@mui/material";
import { PenTool, RotateCcw, Check, X, User, Users } from "lucide-react";
import useTitanApp from "../../hooks/useTitanApp";
import CustomSelect from "../../components/controls/CustomSelect";
import {
  addPatientConsent,
  setAddPatientAIChatMessage,
  setIncrementSignatureCompleted,
  setSignatureDetails,
  trackServiceBilling,
  updateConsultationActions,
  setPatientConsentSignature,
  SaveAIChatMessage,
} from "../../dispatchers/patientDispatchers";
import { relationshipOptions } from "../../lib/patient/addPatientData";
import { setConsultationLoadError } from "../../dispatchers/appDispatchers";
import { actionTypes } from "../../lib/dashboard/consultationsData";
import { ActionType } from "../../enum/ActionType";
import { v4 as uuidv4 } from "uuid";

export default function SignatureModal({ handleClose, isDarkMode }) {
  const { patientState, appState } = useTitanApp();
  const { companyId } = appState || {};
  const {
    selectedPatient,
    consultationId,
    signatureDialog,
    selectedPatientConsultation,
    selectedPatientDetails,
    patientConsentSignature,
  } = patientState || {};
  const patientName = selectedPatient || "Patient";
  const formatedSelectedpatientName = selectedPatientDetails
    ? `${selectedPatientDetails.title || ""} ${selectedPatientDetails.foreName || ""} ${selectedPatientDetails.surName || ""}`.trim()
    : "Patient";

  const open = Boolean(signatureDialog?.open);
  const viewMode = Boolean(signatureDialog?.viewMode);
  const details = signatureDialog?.details || null;

  const patientRelationships = selectedPatientDetails?.relationships
    ? selectedPatientDetails?.relationships?.map((rel, index) => {
        const relationshipLabel =
          relationshipOptions.find(
            (opt) => opt.value === String(rel.relationshipType),
          )?.label || "Other";

        return {
          id: rel.id || index.toString(),
          patientName: rel.name || rel.patientName || "Unknown",
          relationshipType: relationshipLabel,
        };
      })
    : [];
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const defaultConsent =
    "I consent to the pharmacist providing the clinical services discussed and agree to the treatment plan outlined.";
  const [consentText, setConsentText] = useState(defaultConsent);

  const [signerType, setSignerType] = useState("custom");
  const [selectedRelationshipId, setSelectedRelationshipId] = useState("");
  const [customSignerName, setCustomSignerName] = useState("");
  const [customSignerRelationship, setCustomSignerRelationship] = useState("");
  const isConsultationCompleted =
    selectedPatientConsultation?.consultationStatus === 1;

  // Load signature from patientConsentSignature when it changes
  useEffect(() => {
    if (patientConsentSignature && canvasRef.current) {
      try {
        let signatureData = null;

        if (
          typeof patientConsentSignature === "object" &&
          patientConsentSignature.patientSignature
        ) {
          signatureData = patientConsentSignature.patientSignature;
        } else if (typeof patientConsentSignature === "string") {
          signatureData = patientConsentSignature;
        }

        if (signatureData) {
          const img = new window.Image();
          img.onload = () => {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;

            canvas.width = Math.round(rect.width * dpr);
            canvas.height = Math.round(rect.height * dpr);

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);

            ctx.fillStyle = isDarkMode ? "#374151" : "#ffffff";
            ctx.fillRect(0, 0, rect.width, rect.height);

            ctx.drawImage(img, 0, 0, rect.width, rect.height);

            setHasSignature(true);
          };
          img.onerror = () => {
            console.error("Failed to load signature image");
          };
          img.src = signatureData;
        }
      } catch (error) {
        console.error("Error loading signature:", error);
      }
    }
  }, [patientConsentSignature, isDarkMode]);

  useEffect(() => {
    if (!open) {
      setIsDrawing(false);
      setHasSignature(false);
      setConsentText(defaultConsent);
      setSignerType("custom");
      setSelectedRelationshipId("");
      setCustomSignerName("");
      setCustomSignerRelationship("");
      imgRef.current = null;
      return;
    }

    if (viewMode && details) {
      setConsentText(details.consentStatement || defaultConsent);
      const rel = details.patientRelationStatus || {};
      setSignerType(rel.relationShipType === 1 ? "patient" : "custom");
      setCustomSignerName(rel.name || "");
      setCustomSignerRelationship(rel.relationship || "");
      setHasSignature(Boolean(details.patientSignature));
    } else {
      setPatientConsentSignature(null);
      setHasSignature(false);
      setConsentText(defaultConsent);
      setSignerType("custom");
    }
  }, [open, viewMode, details]);

  useEffect(() => {
    if (!open || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    let cancelled = false;
    let resizeObserver = null;

    const waitForCanvasRect = async (maxFrames = 120) => {
      let frames = 0;
      return new Promise((resolve) => {
        const loop = () => {
          const rect = canvas.getBoundingClientRect();
          if ((rect.width > 0 && rect.height > 0) || frames >= maxFrames) {
            resolve(rect);
            return;
          }
          frames++;
          requestAnimationFrame(loop);
        };
        loop();
      });
    };

    const doDraw = (img) => {
      if (cancelled) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      ctx.fillStyle = isDarkMode ? "#374151" : "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);

      if (img) {
        try {
          ctx.drawImage(img, 0, 0, rect.width, rect.height);
        } catch (err) {}
      }
    };

    const setup = async () => {
      await waitForCanvasRect();

      if (cancelled) return;

      let signatureSource = null;

      if (viewMode && details?.patientSignature) {
        signatureSource = details.patientSignature;
      } else if (patientConsentSignature) {
        if (
          typeof patientConsentSignature === "object" &&
          patientConsentSignature.patientSignature
        ) {
          signatureSource = patientConsentSignature.patientSignature;
        } else if (typeof patientConsentSignature === "string") {
          signatureSource = patientConsentSignature;
        }
      }

      if (signatureSource) {
        const img = new window.Image();
        img.onload = () => {
          if (cancelled) return;
          imgRef.current = img;
          setHasSignature(true);
          requestAnimationFrame(() => doDraw(img));
        };
        img.onerror = () => {
          imgRef.current = null;
          requestAnimationFrame(() => doDraw(null));
        };
        img.src = signatureSource;
      } else {
        requestAnimationFrame(() => doDraw(null));
      }

      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => doDraw(imgRef.current));
      });
      resizeObserver.observe(canvas);
    };

    setup();

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [
    open,
    viewMode,
    details?.patientSignature,
    patientConsentSignature,
    isDarkMode,
  ]);

  const getCanvasCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    if (viewMode || isConsultationCompleted) return;
    setIsDrawing(true);
    setHasSignature(true);
    const { x, y } = getCanvasCoords(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (viewMode || !isDrawing) return;
    const { x, y } = getCanvasCoords(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (viewMode || isConsultationCompleted) return;
    setIsDrawing(false);
  };

  const handleTouchStart = (e) => {
    if (viewMode || isConsultationCompleted) return;
    e.preventDefault();
    const touch = e.touches[0];
    startDrawing({
      clientX: touch.clientX,
      clientY: touch.clientY,
      touches: [touch],
    });
  };
  const handleTouchMove = (e) => {
    if (viewMode || isConsultationCompleted) return;
    e.preventDefault();
    const touch = e.touches[0];
    draw({ clientX: touch.clientX, clientY: touch.clientY, touches: [touch] });
  };
  const handleTouchEnd = (e) => {
    if (viewMode || isConsultationCompleted) return;
    e.preventDefault();
    stopDrawing();
  };

  const clearSignature = () => {
    if (viewMode || isConsultationCompleted) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = isDarkMode ? "#374151" : "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    if (viewMode || isConsultationCompleted) return;
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature || !consentText.trim()) return;

    const signatureDataURL = canvas.toDataURL("image/png");

    let relationStatus = {
      name: formatedSelectedpatientName,
      relationship: "Self",
      relationShipType: 1,
    };

    if (signerType === "custom") {
      relationStatus = {
        name: customSignerName || formatedSelectedpatientName,
        relationship: customSignerRelationship || "Other",
        relationShipType: 11,
      };
    } else if (signerType === "relationship" && selectedRelationshipId) {
      const rel = patientRelationships.find(
        (r) => r.id === selectedRelationshipId,
      );
      if (rel) {
        relationStatus = {
          name: rel.patientName,
          relationship: rel.relationshipType,
          relationShipType: 2,
        };
      }
    }

    const payload = {
      signatureId: uuidv4(),
      consentStatement: consentText,
      patientSignature: signatureDataURL,
      patientRelationStatus: relationStatus,
      signedAt: new Date().toISOString(),
    };
    const { patientSignature, ...cleanPayload } = payload;
    const actionsPayload = {
      actionName: ActionType[9],
      actionNumber: "9",
      isPerformed: true,
      payload: JSON.stringify(cleanPayload),
    };
    // The consent API (PatientConsent) keeps SignedAt inside patientRelationStatus;
    // the top-level signedAt is only for the action/chat payloads read by the FE.
    const { signedAt, ...consentFields } = payload;
    const consentRequest = {
      ...consentFields,
      patientRelationStatus: { ...relationStatus, signedAt },
    };
    addPatientConsent(consultationId, consentRequest)
      .then(async (res) => {
        if (res === true) {
          setSignatureDetails(actionsPayload);

          await updateConsultationActions({
            consultationId: consultationId,
            payload: actionsPayload,
          });
          const signatureMessage = [
            {
              sender: "success",
              message: "Signature completed successfully.",
              actionId: 9,
              details: cleanPayload,
            },
          ];
          await SaveAIChatMessage({
            ConsultationChat: JSON.stringify(signatureMessage),
            ConsultationId: consultationId,
          });
          setAddPatientAIChatMessage({
            sender: "success",
            message: "Signature",
            actionId: 9,
            details: cleanPayload,
          });
          setIncrementSignatureCompleted(true);
          if (consultationId) {
            try {
              trackServiceBilling({
                companyId,
                consultationId,
                pharmacyId: selectedPatientDetails?.pharmacyId,
                actiontype: "patient signature",
              });
            } catch (err) {
              console.error(
                "Error calling billing tracker for signature:",
                err,
              );
            }
          }
        }
      })
      .catch((error) => {
        console.error("Error adding patient consent:", error);
        setConsultationLoadError(true);
      });

    handleClose && handleClose();
    setHasSignature(false);
    setSignerType("patient");
    setSelectedRelationshipId("");
    setCustomSignerName("");
    setCustomSignerRelationship("");
    setConsentText(defaultConsent);
  };

  return (
    <Modal open={open} onClose={handleClose} keepMounted>
      <Box className="signature-modal">
        <div className="header">
          <h2>Patient Consent & Signature</h2>
          {viewMode && <small style={{ marginLeft: 12 }}>View only</small>}
        </div>

        <div className="content">
          <div
            className={`${isDarkMode ? "patient-info-dark" : "patient-info"}`}
          >
            <PenTool size={16} className="icon" />
            <span>Patient: {formatedSelectedpatientName}</span>
          </div>

          <div className="signer-section">
            <label className="section-label">
              <User size={16} /> Who is providing the signature?
            </label>

            <div className="option">
              <input
                type="radio"
                id="signer-patient"
                name="signerType"
                value="patient"
                checked={signerType === "patient"}
                onChange={(e) => setSignerType(e.target.value)}
                disabled={viewMode}
              />
              <label htmlFor="signer-patient">
                Current Patient ({formatedSelectedpatientName})
              </label>
            </div>

            <div className="option">
              <input
                type="radio"
                id="signer-relationship"
                name="signerType"
                value="relationship"
                checked={signerType === "relationship"}
                onChange={(e) => setSignerType(e.target.value)}
                disabled={viewMode || patientRelationships.length === 0}
              />
              <label
                htmlFor="signer-relationship"
                style={{ opacity: patientRelationships.length === 0 ? 0.6 : 1 }}
              >
                <Users size={14} /> Relationship
              </label>
              {patientRelationships.length === 0 && (
                <span className="no-relations">
                  (No relationships recorded)
                </span>
              )}
            </div>

            <div className="option">
              <input
                type="radio"
                id="signer-custom"
                name="signerType"
                value="custom"
                checked={signerType === "custom"}
                onChange={(e) => setSignerType(e.target.value)}
                disabled={viewMode}
              />
              <label htmlFor="signer-custom">Other Person</label>
            </div>
          </div>

          {signerType === "custom" && (
            <div className="custom-inputs">
              <input
                placeholder="Name"
                value={customSignerName}
                onChange={(e) => setCustomSignerName(e.target.value)}
                disabled={viewMode}
              />
              <input
                placeholder="Relationship"
                value={customSignerRelationship}
                onChange={(e) => setCustomSignerRelationship(e.target.value)}
                disabled={viewMode}
              />
            </div>
          )}

          {patientRelationships.length > 0 && signerType === "relationship" && (
            <div className="custom-select-wrapper">
              <CustomSelect
                label="Select relationship"
                value={selectedRelationshipId}
                options={patientRelationships}
                onChange={(selected) => setSelectedRelationshipId(selected.id)}
                getLabel={(val) => {
                  const opt =
                    typeof val === "string"
                      ? patientRelationships.find((r) => r.id === val)
                      : val;
                  return opt
                    ? `${opt.patientName} (${opt.relationshipType})`
                    : "Select";
                }}
                isDisabled={viewMode}
              />
            </div>
          )}

          <div className="consent">
            <label>Consent Statement</label>
            <textarea
              value={consentText}
              onChange={(e) => setConsentText(e.target.value)}
              disabled={viewMode}
            />
          </div>

          <div className="signature-pad">
            <div className="pad-header">
              <label>Patient Signature</label>
              {!viewMode && (
                <button onClick={clearSignature} className="outline-btn">
                  <RotateCcw size={14} /> Clear
                </button>
              )}
            </div>

            <canvas
              ref={canvasRef}
              className="canvas"
              style={{
                backgroundColor: isDarkMode ? "#374151" : "#fff",
                touchAction: viewMode ? "none" : "auto",
                width: "100%",
                height: 180,
              }}
              onMouseDown={!viewMode ? startDrawing : undefined}
              onMouseMove={!viewMode ? draw : undefined}
              onMouseUp={!viewMode ? stopDrawing : undefined}
              onMouseLeave={!viewMode ? stopDrawing : undefined}
              onTouchStart={!viewMode ? handleTouchStart : undefined}
              onTouchMove={!viewMode ? handleTouchMove : undefined}
              onTouchEnd={!viewMode ? handleTouchEnd : undefined}
            />
            <p className="hint">
              {viewMode
                ? "Viewing saved signature"
                : "Sign above using your mouse or finger on touch devices"}
            </p>
          </div>

          <div className="signature-actions">
            {viewMode ? (
              <button className="outline-btn" onClick={handleClose}>
                <X size={14} /> Close
              </button>
            ) : (
              <>
                <button className="outline-btn" onClick={handleClose}>
                  <X size={14} /> Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={saveSignature}
                  disabled={
                    !hasSignature ||
                    !consentText.trim() ||
                    (signerType === "relationship" &&
                      !selectedRelationshipId) ||
                    (signerType === "custom" &&
                      (!customSignerName.trim() ||
                        !customSignerRelationship.trim()))
                  }
                >
                  <Check size={14} /> Save Signature
                </button>
              </>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
}
